import { useState, useContext, useEffect, useMemo, useRef, useDebugValue, } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';
import { MobileContext } from '../Context/MobileContext';
import { makeGetPrevLoadDims } from './redux/selectors';
import { updateMeasuredCookie } from './redux/actions';
import { useDeepEqualMemo } from '../utils/hooks/useDeepEqualMemo';
var logger = ddt.createLogger('measuredhook');
export var DEFAULT_SIZE = {
    desktop: { width: 1024, height: 768 },
    mobile: { width: 375, height: 667 },
};
var THROTTLE_TIMEOUT = 500;
var THROTTLE_OPTIONS = { trailing: true };
function isServerRendering() {
    return typeof document === 'undefined' || typeof window === 'undefined';
}
export function useMeasuredCookie(cookieType, containerRef, _a) {
    var _b = _a.defaultSize, defaultSizeProp = _b === void 0 ? DEFAULT_SIZE : _b, _c = _a.cookieDim, cookieDim = _c === void 0 ? 'both' : _c, listenOnWindow = _a.listenOnWindow, measureWindow = _a.measureWindow, sentinelRef = _a.sentinelRef;
    var getPrevLoadDims = useRef(makeGetPrevLoadDims());
    var prevLoadDims = useSelector(function (state) {
        return cookieType
            ? getPrevLoadDims.current(state, cookieType, cookieDim)
            : { width: 0, height: 0 };
    });
    var dispatch = useDispatch();
    var isMobile = useContext(MobileContext);
    var defaultSize = useDeepEqualMemo(isMobile ? defaultSizeProp.mobile : defaultSizeProp.desktop);
    var _d = useState(function () {
        var cont = sentinelRef || isServerRendering()
            ? prevLoadDims.width > 0 || prevLoadDims.height > 0
                ? prevLoadDims
                : defaultSize
            : undefined;
        logger.log('Measured init', {
            hasSentinel: !!sentinelRef,
            isSSR: isServerRendering(),
            prevLoadDims: prevLoadDims,
            defaultSize: defaultSize,
            containerSize: cont,
        });
        return cont;
    }), containerSize = _d[0], setContainerSize = _d[1];
    useDebugValue(containerSize, function (size) { return "Width: " + (size === null || size === void 0 ? void 0 : size.width) + ", height: " + (size === null || size === void 0 ? void 0 : size.height); });
    var updateContainerSize = useMemo(function () {
        return throttle(function () {
            function getContainerSize() {
                var _a;
                // We are in browser from here
                if (measureWindow) {
                    return {
                        width: window.innerWidth,
                        height: window.innerHeight,
                    };
                }
                var containerElement = containerRef.current;
                var rect = containerElement === null || containerElement === void 0 ? void 0 : containerElement.getBoundingClientRect();
                if (!rect || (!rect.width && !rect.height)) {
                    return defaultSize;
                }
                var sentinelWidth = (_a = sentinelRef === null || sentinelRef === void 0 ? void 0 : sentinelRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width;
                return {
                    // Make sure container is never bigger than parent
                    // this allows us to shrink the container :)
                    width: sentinelWidth && sentinelWidth < rect.width
                        ? sentinelWidth
                        : rect.width,
                    height: rect.height,
                };
            }
            var newSize = getContainerSize();
            var widthChanged = ['width', 'both'].includes(cookieDim) &&
                (!containerSize || newSize.width !== containerSize.width);
            var heightChanged = ['height', 'both'].includes(cookieDim) &&
                (!containerSize || newSize.height !== containerSize.height);
            if (widthChanged || heightChanged) {
                logger.log('Updated measurements', newSize);
                setContainerSize(newSize);
                cookieType &&
                    dispatch(updateMeasuredCookie(cookieType, cookieDim, newSize));
            }
        }, THROTTLE_TIMEOUT, THROTTLE_OPTIONS);
    }, [
        containerSize,
        cookieDim,
        containerRef,
        measureWindow,
        sentinelRef,
        defaultSize,
        dispatch,
        cookieType,
    ]);
    useEffect(function () {
        updateContainerSize();
        if (listenOnWindow) {
            window.addEventListener('resize', updateContainerSize);
            return function () { return window.removeEventListener('resize', updateContainerSize); };
        }
    }, [updateContainerSize, listenOnWindow]);
    return containerSize;
}
//# sourceMappingURL=useMeasuredCookie.js.map